import { useCallback } from "react";
import { toast } from "react-toastify";

import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import buildCommentMap from "../../utils/ratings/buildCommentMap";
import buildCommentPayload from "../../utils/ratings/buildCommentPayload";
import buildGroupInfoDto from "../../utils/ratings/buildGroupInfoDto";

/**
 * Handles the comment save path for both HOMEROOM and GROUPS modes.
 *
 * Save strategy: DELTA only — only records whose comment has changed vs.
 * the last known server state (existingComments) are sent. This is
 * intentional server behavior and must NOT be unified with the full-state
 * ratings save strategy.
 *
 * After a successful save, syncFromServer is called with the fresh server
 * payload to update the delta baseline without touching the UI state.
 *
 * @param {Object}      params
 * @param {"HOMEROOM"|"GROUPS"} params.mode
 * @param {Object}      params.comments             - from useCommentState
 * @param {Object}      [params.fluencyByKey]       - readingFluency WPM from useCommentState
 * @param {Array}       params.existingComments      - from useCommentState (server baseline)
 * @param {Object|null} params.groupContext           - GroupContext (GROUPS) or null (HOMEROOM)
 * @param {Object}      params.userDetails           - {uid}
 * @param {string}      params.token
 * @param {Function}    params.setHasCommentChanges  - from useCommentState
 * @param {Function}    params.syncFromServer        - from useCommentState; updates delta baseline
 *
 * @returns {{ saveComments: Function }}
 */
const useCommentSave = ({
    mode,
    comments,
    fluencyByKey = {},
    existingComments,
    groupContext,
    userDetails,
    token,
    setHasCommentChanges,
    syncFromServer
}) => {
    const saveComments = useCallback(async () => {
        if (!token) return;

        const updaterGuid = userDetails?.uid ?? null;

        const commentMap = buildCommentMap({ comments, fluencyByKey });
        const payload = buildCommentPayload({
            commentMap,
            existingComments,
            updaterGuid
        });

        if (payload.length === 0) {
            toast.info("There are no comment changes to save.", { autoClose: 2000 });
            setHasCommentChanges(false);
            return;
        }

        try {
            let response;

            if (mode === "GROUPS") {
                if (!groupContext) {
                    toast.error("Group context is not available. Please reload and try again.");
                    return;
                }

                const groupInfoDto = buildGroupInfoDto({
                    ownProfile: groupContext.ownProfile,
                    sharedGroups: groupContext.sharedGroups
                });

                response = await ElementaryProgressReportDao({
                    action: "updateGroupStudentCriteriaComments",
                    data: {
                        groupInfoDto,
                        contentStudentComments: payload
                    },
                    token
                });
            } else {
                // HOMEROOM — gradingCriteriaId required on the URL.
                // All HOMEROOM comment records share the same gradingCriteria
                // (all students in the same EPR have the same criteria for this template).
                const gradingCriteriaId = payload[0]?.gradingCriteria;

                if (!gradingCriteriaId) {
                    toast.error("Grading criteria not found. Please reload and try again.");
                    return;
                }

                response = await ElementaryProgressReportDao({
                    action: "updateStudentCriteriaComments",
                    gradingCriteriaId: String(gradingCriteriaId),
                    data: payload,
                    token
                });
            }

            if (response?.data?.payload) {
                // Sync the delta baseline with the fresh server state.
                // Does NOT overwrite UI comments — mid-flight edits are preserved;
                // dirty is recomputed against the new baseline.
                syncFromServer(response.data.payload);
                toast.success("Comment(s) successfully saved.", { autoClose: 3000 });
            } else {
                toast.error("Comments were not saved. Please make note of your comments and try again later.", {
                    autoClose: 3000
                });
            }
        } catch (err) {
            console.error("useCommentSave: saveComments failed:", err);
            toast.error("Comments were not saved. Please make note of your comments and try again later.");
        }
    }, [
        mode,
        comments,
        fluencyByKey,
        existingComments,
        groupContext,
        userDetails,
        token,
        setHasCommentChanges,
        syncFromServer
    ]);

    return { saveComments };
};

export default useCommentSave;
